/**
 * CasualCompetitors 页面常量配置
 */
import { PageLogMes } from '@home/utils/buriedPointCfg';

// 实体类型
export const ENTITY_TYPE = 'mobile';

export const DEFAULT_SENSORTOWER_DATA_SOURCE = 'sensortower';

export const getDataSourceMetricKey = (dataSource: string, metric: string): string => {
  return `${dataSource}__${metric}`;
};

export const getDataSourceMetricRatioKey = (dataSource: string, metric: string): string => {
  return `${getDataSourceMetricKey(dataSource, metric)}_ratio`;
};

export const getDataSourceCustomFieldKey = (dataSource: string, field: string): string => {
  return `${dataSource}_${field}`;
};

export const getDataSourceMetrics = (dataSource: string, metrics: string[]): string[] => {
  return metrics.map((metric) => getDataSourceMetricKey(dataSource, metric));
};

export const getMetricOrderBy = (dataSource: string, metric: string, order: 'asc' | 'desc' = 'desc'): string => {
  return `${getDataSourceMetricKey(dataSource, metric)} ${order}`;
};

// 最大分页大小（一次性拉取全量数据）
export const MAX_PAGE_SIZE = 100000;

// 埋点配置
export const CASUAL_COMPETITORS_PAGE_LOG_CFG = PageLogMes.studioCasualHubCompetitors;

// 品类Tab选项（i18n key）
export const TAB_OPTIONS_FIRST: Array<{ text: string; value: string }> = [
  { text: 'dbh_studio_casual_tab_general', value: 'general_casual' },
  { text: 'dbh_studio_casual_tab_iaa', value: 'iaa' },
  { text: 'dbh_studio_casual_tab_match3', value: 'match_3' },
  { text: 'dbh_studio_casual_tab_merge2', value: 'merge_2' },
  { text: 'dbh_studio_casual_tab_luck_battle', value: 'lucky_battle' },
  { text: 'dbh_studio_casual_tab_hybrid_casual', value: 'hybrid_casual' },
  { text: 'dbh_studio_casual_tab_simulation', value: 'simulation' },
  { text: 'dbh_studio_casual_tab_rpg_idler', value: 'rpg_tower_defense' },
];

// IAA子品类Tab选项（i18n key）
export const TAB_OPTIONS_SECOND: Array<{ text: string; value: string }> = [
  { text: 'dbh_studio_casual_tab_mahjong', value: 'iaa_mahjong' },
  { text: 'dbh_studio_casual_tab_arrows', value: 'iaa_arrows' },
  { text: 'dbh_studio_casual_tab_iaa_all', value: 'iaa_all' },
];

// 二合子品类Tab选项（i18n key）：仅选中二合时展示
// 全部二合沿用既有 genre_id=merge_2，保证接口兼容；剧情二合使用 merge_2_narrative
export const TAB_OPTIONS_MERGE2_SECOND: Array<{ text: string; value: string }> = [
  { text: 'dbh_studio_casual_tab_merge2_all', value: 'merge_2' },
  { text: 'dbh_studio_casual_tab_merge2_narrative', value: 'merge_2_narrative' },
];

/** 拥有二级品类 Tab 的主品类 */
export const PRIMARY_TABS_WITH_SECONDARY = ['iaa', 'merge_2'] as const;

export const hasSecondaryGenreTabs = (primaryTab: string): boolean => {
  return (PRIMARY_TABS_WITH_SECONDARY as readonly string[]).includes(primaryTab);
};

export const getSecondaryTabOptions = (
  primaryTab: string,
): Array<{ text: string; value: string }> => {
  if (primaryTab === 'iaa') {
    return TAB_OPTIONS_SECOND;
  }
  if (primaryTab === 'merge_2') {
    return TAB_OPTIONS_MERGE2_SECOND;
  }
  return [];
};

// 品类Tab Bubble提示文案
export const TAB_BUBBLE_KEY: IRecord<string> = {
  general_casual: 'home_studio_casual_competitors_genre_type_tips_general_casual',
  iaa: 'home_studio_casual_competitors_genre_type_tips_iaa',
  match_3: 'home_studio_casual_competitors_genre_type_tips_match3',
  merge_2: 'home_studio_casual_competitors_genre_type_tips_merge2',
  lucky_battle: 'home_studio_casual_competitors_genre_type_tips_lucky_battle',
  hybrid_casual: 'home_studio_casual_competitors_genre_type_tips_hybrid_casual',
  simulation: 'home_studio_casual_competitors_genre_type_tips_simulation',
  rpg_tower_defense: 'home_studio_casual_competitors_genre_type_tips_rpgtd',
  iaa_mahjong: 'home_studio_casual_competitors_genre_type_tips_iaa_mahjong',
  iaa_arrows: 'home_studio_casual_competitors_genre_type_tips_iaa_arrows',
  iaa_all: 'home_studio_casual_competitors_genre_type_tips_iaa_all',
};

// 关注游戏Tab选项（value 直接对应排序指标 metric key）
export const KEY_GAMES_TAB_OPTIONS = [
  { text: 'dbh_studio_casual_key_games_tab_active', value: 'daily_avg_dau' },
  { text: 'dbh_studio_casual_key_games_tab_income', value: 'iap_revenue' },
  { text: 'dbh_studio_casual_key_games_tab_download', value: 'download' },
];

// KPI指标键名
export const getKpiMetricKeys = (dataSource: string) => {
  return {
    // 月粒度
    MONTHLY_AVG_DAU: getDataSourceMetricKey(dataSource, 'daily_avg_dau'),
    MONTHLY_IAP: getDataSourceMetricKey(dataSource, 'iap_revenue'),
    MONTHLY_DOWNLOAD: getDataSourceMetricKey(dataSource, 'download'),
    // 日粒度
    DAILY_DAU: getDataSourceMetricKey(dataSource, 'dau'),
    DAILY_IAP: getDataSourceMetricKey(dataSource, 'iap_revenue'),
    DAILY_DOWNLOAD: getDataSourceMetricKey(dataSource, 'download'),
  };
};

export const KPI_METRIC_KEYS = getKpiMetricKeys(DEFAULT_SENSORTOWER_DATA_SOURCE);

// KPI卡片配置项类型
export interface IKpiCardConfigItem {
  metricKey: string;
  title: string;
  tipsKey: string;
}

// 品类大盘KPI卡片配置（根据日期粒度）
export const getKpiCardsConfig = (dataSource: string): IRecord<IKpiCardConfigItem[]> => {
  const metricKeys = getKpiMetricKeys(dataSource);

  return {
    // 月粒度：月平均日活跃、月IAP收入、月下载
    monthly: [
      {
        metricKey: metricKeys.MONTHLY_AVG_DAU,
        title: 'dbh_studio_casual_category_kpi_monthly_dau',
        tipsKey: 'monthly_avg_dau',
      },
      {
        metricKey: metricKeys.MONTHLY_IAP,
        title: 'dbh_studio_casual_category_kpi_monthly_iap',
        tipsKey: 'monthly_iap',
      },
      {
        metricKey: metricKeys.MONTHLY_DOWNLOAD,
        title: 'dbh_studio_casual_category_kpi_monthly_download',
        tipsKey: 'monthly_download',
      },
    ],
    // 日粒度：日活跃、日IAP收入、日下载
    daily: [
      {
        metricKey: metricKeys.DAILY_DAU,
        title: 'dbh_studio_casual_category_kpi_daily_dau',
        tipsKey: 'daily_dau',
      },
      {
        metricKey: metricKeys.DAILY_IAP,
        title: 'dbh_studio_casual_category_kpi_daily_iap',
        tipsKey: 'daily_iap',
      },
      {
        metricKey: metricKeys.DAILY_DOWNLOAD,
        title: 'dbh_studio_casual_category_kpi_daily_download',
        tipsKey: 'daily_download',
      },
    ],
  };
};

export const KPI_CARDS_CONFIG = getKpiCardsConfig(DEFAULT_SENSORTOWER_DATA_SOURCE);

// 默认Tab值
export const DEFAULT_PRIMARY_TAB = 'iaa';
export const DEFAULT_SECONDARY_TAB = 'iaa_mahjong';
export const DEFAULT_MERGE2_SECONDARY_TAB = 'merge_2';

export const getDefaultSecondaryTabByPrimary = (primaryTab: string): string => {
  if (primaryTab === 'iaa') {
    return DEFAULT_SECONDARY_TAB;
  }
  if (primaryTab === 'merge_2') {
    return DEFAULT_MERGE2_SECONDARY_TAB;
  }
  return '';
};

// 模块切换Tab选项
export const MODULE_TAB_OPTIONS = [
  {
    text: 'dbh_studio_casual_top_games',
    value: 'top_games',
    bubblePlacement: 'top-start',
    bubble: 'dbh_studio_casual_top_games_bubble',
  },
  {
    text: 'dbh_studio_casual_dark_horse_title',
    value: 'rising_games',
    bubblePlacement: 'top',
    bubble: 'dbh_studio_casual_rising_games_bubble',
  },
];

// 默认模块Tab
export const DEFAULT_MODULE_TAB = 'top_games';

// IAA品类列表（不显示收入指标）
export const IAA_GENRES = ['iaa_mahjong', 'iaa_arrows', 'iaa_all'];

// 厂商KPI指标（月粒度/日粒度共用）
export const getPublisherKpiMetrics = (dataSource: string): string[] => {
  return [...getDataSourceMetrics(dataSource, ['daily_avg_dau', 'iap_revenue', 'download']), 'company_game_count'];
};

export const PUBLISHER_KPI_METRICS = getPublisherKpiMetrics(DEFAULT_SENSORTOWER_DATA_SOURCE);

// 厂商游戏表格指标
export const getPublisherGameMetrics = (dataSource: string): string[] => {
  return getDataSourceMetrics(dataSource, ['daily_avg_dau', 'iap_revenue', 'download', 'dau_share']);
};

export const PUBLISHER_GAME_METRICS = getPublisherGameMetrics(DEFAULT_SENSORTOWER_DATA_SOURCE);

// 分页sizeOptions
export const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 50, 100];

// 默认分页size
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

// ==================== 指标解释配置 ====================
// 品类大盘 KPI 指标解释 i18n key 配置
export const CATEGORY_KPI_TIPS_I18N: IRecord<string> = {
  monthly_avg_dau: 'dbh_studio_casual_kpi_monthly_avg_dau_tips',
  monthly_iap: 'dbh_studio_casual_kpi_cumulative_monthly_iap_tips',
  monthly_download: 'dbh_studio_casual_kpi_cumulative_monthly_download_tips',
  daily_dau: 'dbh_studio_casual_kpi_daily_dau_tips',
  daily_iap: 'dbh_studio_casual_kpi_cumulative_daily_iap_tips',
  daily_download: 'dbh_studio_casual_kpi_cumulative_daily_download_tips',
};

// 重点游戏表格指标解释 i18n key 配置（按粒度区分）
export const KEY_GAMES_TABLE_TIPS_I18N: IRecord<IRecord<string>> = {
  monthly: {
    daily_avg_dau: 'dbh_studio_casual_keygames_monthly_dau_tips',
    iap_revenue: 'dbh_studio_casual_keygames_monthly_iap_tips',
    download: 'dbh_studio_casual_keygames_monthly_download_tips',
    dau_share: 'dbh_studio_casual_keygames_monthly_active_ratio_tips',
  },
  daily: {
    daily_avg_dau: 'dbh_studio_casual_keygames_daily_dau_tips',
    iap_revenue: 'dbh_studio_casual_keygames_daily_iap_tips',
    download: 'dbh_studio_casual_keygames_daily_download_tips',
    dau_share: 'dbh_studio_casual_keygames_daily_active_ratio_tips',
  },
};

// 头部厂商 KPI 指标解释 i18n key 配置（按粒度区分）
export const TOP_PUBLISHERS_KPI_TIPS_I18N: IRecord<{
  dau: string;
  iap: string;
  download: string;
}> = {
  monthly: {
    dau: 'dbh_studio_casual_publisher_kpi_monthly_dau_tips',
    iap: 'dbh_studio_casual_publisher_kpi_monthly_iap_tips',
    download: 'dbh_studio_casual_publisher_kpi_monthly_download_tips',
  },
  daily: {
    dau: 'dbh_studio_casual_publisher_kpi_daily_dau_tips',
    iap: 'dbh_studio_casual_publisher_kpi_daily_iap_tips',
    download: 'dbh_studio_casual_publisher_kpi_daily_download_tips',
  },
};

// 头部厂商表格指标解释 i18n key 配置（按粒度区分）
export const TOP_PUBLISHERS_TABLE_TIPS_I18N: IRecord<IRecord<string>> = {
  monthly: {
    daily_avg_dau: 'dbh_studio_casual_publisher_table_monthly_dau_tips',
    iap_revenue: 'dbh_studio_casual_publisher_table_monthly_iap_tips',
    download: 'dbh_studio_casual_publisher_table_monthly_download_tips',
    dau_share: 'dbh_studio_casual_publisher_table_monthly_active_ratio_tips',
  },
  daily: {
    daily_avg_dau: 'dbh_studio_casual_publisher_table_daily_dau_tips',
    iap_revenue: 'dbh_studio_casual_publisher_table_daily_iap_tips',
    download: 'dbh_studio_casual_publisher_table_daily_download_tips',
    dau_share: 'dbh_studio_casual_publisher_table_daily_active_ratio_tips',
  },
};
